import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useAuth } from '../../hooks/useAuth';
import { obtenerCasoPorId } from '../../services/casoService';
import { obtenerClientePorId } from '../../services/clienteService';
import { obtenerVerificacionPorCaso } from '../../services/verificacionService';
import { decryptId } from '../../utils/encrypt';
import ChecklistVerificacion from '../../components/common/ChecklistVerificacion';
import SubirDocumento from '../../components/common/SubirDocumento';
import ListaDocumentos from '../../components/common/ListaDocumentos';
import InformePDF from '../../components/common/InformePDF';
import AdminLayout from '../../components/layout/AdminLayout';
import BackButton from '../../components/common/BackButton';
// Eliminar esta línea: import GestionPagos from '../../components/common/GestionPagos';
import './DetalleCasoAdmin.css';

const DetalleCasoAdmin = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [caso, setCaso] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [verificacion, setVerificacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubirDocumento, setShowSubirDocumento] = useState(false);
  const [refreshDocumentos, setRefreshDocumentos] = useState(0);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const realId = decryptId(id);
      const casoData = await obtenerCasoPorId(realId);
      if (!casoData) {
        alert('Caso no encontrado');
        navigate('/casos');
        return;
      }
      setCaso(casoData);
      
      if (casoData.clienteId) {
        const clienteData = await obtenerClientePorId(casoData.clienteId);
        setCliente(clienteData);
      }
      
      const verifData = await obtenerVerificacionPorCaso(casoData.id);
      setVerificacion(verifData);
      
    } catch (error) {
      console.error('Error:', error);
      alert('Error al cargar datos: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    cargarDatos();
  }, [user, id, navigate, cargarDatos]);

  const handleDocumentoSubido = () => {
    setShowSubirDocumento(false);
    setRefreshDocumentos(prev => prev + 1);
  };

  const getEstadoBadge = (estado) => {
    const clases = {
      nuevo: 'badge-nuevo',
      en_verificacion: 'badge-verificacion',
      en_proceso: 'badge-proceso',
      completado: 'badge-completado',
      cancelado: 'badge-cancelado'
    };
    const textos = {
      nuevo: 'Nuevo',
      en_verificacion: 'En Verificacion',
      en_proceso: 'En Proceso',
      completado: 'Completado',
      cancelado: 'Cancelado'
    };
    return <span className={`estado-badge ${clases[estado]}`}>{textos[estado]}</span>;
  };

  if (loading) return <div className="loading">Cargando detalle del caso...</div>;
  if (!caso) return <div className="error">Caso no encontrado</div>;

  return (
    <AdminLayout>
      <div className="detalle-caso-admin">
        <div className="detalle-header">
          <BackButton to="/casos" label="Volver a Casos" variant="outline" />
          <div className="header-titulo">
            <h1>Detalle del Caso Legal</h1>
            {getEstadoBadge(caso.estado)}
          </div>
        </div>

        <div className="detalle-grid">
          {/* Informacion del Cliente */}
          <div className="info-card">
            <h2>Informacion del Cliente</h2>
            {cliente ? (
              <div className="info-content">
                <p><strong>Nombre:</strong> {cliente.nombre}</p>
                <p><strong>Documento:</strong> {cliente.tipoDocumento?.toUpperCase()} {cliente.documento}</p>
                <p><strong>Email:</strong> {cliente.email}</p>
                <p><strong>Telefono:</strong> {cliente.telefono}</p>
                {cliente.direccion && <p><strong>Direccion:</strong> {cliente.direccion}</p>}
              </div>
            ) : (
              <p>No hay informacion del cliente</p>
            )}
          </div>

          {/* Informacion del Vehiculo */}
          <div className="info-card">
            <h2>Informacion del Vehiculo</h2>
            <div className="info-content">
              <p><strong>Marca:</strong> {caso.vehiculo?.marca || 'No especificada'}</p>
              <p><strong>Modelo:</strong> {caso.vehiculo?.modelo || 'No especificado'}</p>
              <p><strong>Placa:</strong> {caso.vehiculo?.placa?.toUpperCase() || 'No especificada'}</p>
              <p><strong>Anio:</strong> {caso.vehiculo?.año || 'No especificado'}</p>
              {caso.vehiculo?.precio && (
                <p><strong>Precio:</strong> ${Number(caso.vehiculo.precio).toLocaleString()}</p>
              )}
            </div>
          </div>

          {/* Informacion del Servicio */}
          <div className="info-card">
            <h2>Informacion del Servicio</h2>
            <div className="info-content">
              <p><strong>Tipo:</strong> {
                caso.tipoServicio === 'compra' ? 'Asesoria en Compra' :
                caso.tipoServicio === 'venta' ? 'Asesoria en Venta' :
                caso.tipoServicio === 'verificacion' ? 'Verificacion Legal' : 'Tramite de Traspaso'
              }</p>
              <p><strong>Fecha:</strong> {caso.fechaCreacion?.toLocaleDateString()}</p>
            </div>
          </div>

          {/* Notas Internas */}
          {caso.notas && (
            <div className="info-card notas-card">
              <h2>Notas Internas</h2>
              <div className="info-content">
                <p>{caso.notas}</p>
              </div>
            </div>
          )}
        </div>

        {/* Checklist de Verificacion */}
        <div className="checklist-section">
          <ChecklistVerificacion casoId={caso.id} />
        </div>

        {/* Documentos */}
        <div className="documentos-section">
          <div className="documentos-header">
            <h2>Documentos del Caso</h2>
            <button className="btn-subir-docs" onClick={() => setShowSubirDocumento(true)}>
              + Subir Documento
            </button>
          </div>

          {showSubirDocumento && (
            <div className="modal-overlay" onClick={() => setShowSubirDocumento(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowSubirDocumento(false)}>×</button>
                <SubirDocumento
                  casoId={caso.id}
                  onDocumentoSubido={handleDocumentoSubido}
                  onCancel={() => setShowSubirDocumento(false)}
                />
              </div>
            </div>
          )}

          <ListaDocumentos casoId={caso.id} key={refreshDocumentos} />
        </div>

        {/* Acciones */}
        <div className="detalle-actions">
          <PDFDownloadLink
            document={<InformePDF caso={caso} cliente={cliente} verificacion={verificacion} />}
            fileName={`Informe_GoldenCars_${caso.vehiculo?.placa || 'caso'}_${new Date().toISOString().split('T')[0]}.pdf`}
          >
            {({ loading }) => (
              <button className="btn-generar-informe" disabled={loading}>
                {loading ? 'Generando PDF...' : 'Generar Informe PDF'}
              </button>
            )}
          </PDFDownloadLink>
        </div>
      </div>
    </AdminLayout>
  );
};

export default DetalleCasoAdmin;